import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useCallback, useEffect, useRef } from "react";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { LoadingPanel } from "../../components/layout/Loading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import { closed } from "../../components/drawer/drawerSlice";
import { OpportunityPriority, STATUSARRAY } from "../../_contstants/common";
import {
  createOpportunity,
  getAllOpportunities,
  getOpportunityById,
  updateOpportunity,
} from "./services/opportunity.services";
import { clearOpportunityDetails } from "./opportunitySlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import moment from "moment";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { findAllActiveOpportunityType } from "../opportunitytype/services/opportunityType.services";
import { IOpportunityType } from "../opportunitytype/opportunityTypeModel";
import { IUser } from "../user/userModel";
import FormDatePicker from "../../components/formFields/FormDateField";
import { getAllLeads } from "../clientForm/services/services.clientform";
import FormIncrementalSearch from "../../components/formFields/FormIncrementalSearch";
import { getAllActiveUsers } from "../user/services/user.services";
import { useLocation } from "react-router-dom";
import { getAllDashboardOpportunities } from "../dashboard/services/dashboard.services";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const UserChangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const OpportunityGUID = useAppSelector((state) => state.drawer.data);
  const UserIDString = useAppSelector(
    (state) => state.login.loginDetails.UserID
  );
  const UserID = parseInt(UserIDString);

  useEffect(() => {
    if (!OpportunityGUID) {
      formRenderProps.onChange("AssignTeamID", {
        value: UserID ? +UserID : null,
      });
    }
  }, [OpportunityGUID]);

  return null;
};

const CreateOpportunity: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const PathName = location?.pathname?.split("/")[1];

  const gridRef = useRef<any>(null);
  const OpportunityDetail = useAppSelector(
    (state) => state.opportunity.OpportunityDetail
  );
  const OpportunityGUID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.opportunity.formLoading);
  const UserList = useAppSelector((state) => state.user.UserList);
  const OpportunityTypeList = useAppSelector(
    (state) => state.opportunityType.OpportunityTypeList
  );

  useEffect(() => {
    dispatch(findAllActiveOpportunityType());
    dispatch(getAllActiveUsers());
    return () => {
      dispatch(clearOpportunityDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (OpportunityGUID) {
      dispatch(getOpportunityById(OpportunityGUID));
    }
  }, [OpportunityGUID]);

  const handleClientSearchChange = useCallback(
    async (search: string, field: string, formRenderProps: FormRenderProps) => {
      const payload = { GroupCode: search, FirstName: search, IsParent: false };
      const result = await dispatch(getAllLeads(payload));
      formRenderProps.onChange(field, {
        value: result.payload,
      });
    },
    [dispatch]
  );

  const handleSubmit = async (values: any) => {
    try {
      if (!OpportunityGUID) {
        const insertPayload = {
          AddCustomerID: values?.AddCustomerID ? +values?.AddCustomerID : null,
          OpportunityTypeID: values?.OpportunityTypeID
            ? +values?.OpportunityTypeID
            : null,
          ExpectedValue: values?.ExpectedValue ? +values?.ExpectedValue : null,
          SetPriority: values?.SetPriority ? values?.SetPriority : "",
          FollowupDate: values?.FollowupDate
            ? moment(values?.FollowupDate)?.format("YYYY-MM-DD")
            : "",
          AssignTeamID: values?.AssignTeamID ? +values?.AssignTeamID : null,
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(createOpportunity(insertPayload));
        if (response?.payload?.Status === 201) {
          dispatch(closed());
          dispatch(clearOpportunityDetails());
          if (PathName === "opportunity") {
            dispatch(getAllOpportunities());
          } else {
            dispatch(getAllDashboardOpportunities());
          }
        }
      } else {
        const editPayload = {
          OpportunityGUID: OpportunityGUID,
          AddCustomerID: values?.AddCustomerID ? +values?.AddCustomerID : null,
          OpportunityTypeID: values?.OpportunityTypeID
            ? +values?.OpportunityTypeID
            : null,
          ExpectedValue: values?.ExpectedValue ? +values?.ExpectedValue : null,
          SetPriority: values?.SetPriority ? values?.SetPriority : "",
          FollowupDate: values?.FollowupDate
            ? moment(values?.FollowupDate)?.format("YYYY-MM-DD")
            : "",
          AssignTeamID: values?.AssignTeamID ? +values?.AssignTeamID : null,
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(updateOpportunity(editPayload));
        if (response?.payload?.Status === 200) {
          dispatch(closed());
          dispatch(clearOpportunityDetails());
          if (PathName === "opportunity") {
            dispatch(getAllOpportunities());
          } else {
            dispatch(getAllDashboardOpportunities());
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={OpportunityDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="drawer-header">
              <Typography.h4 className="m-0">
                {OpportunityGUID ? "Update Opportunity" : "Create Opportunity"}
              </Typography.h4>
              <Button
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                <RxCross1 />
              </Button>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="AddCustomerID"
                label="Client"
                placeholder="i.e.John Doe"
                component={FormIncrementalSearch}
                validator={requiredValidator}
                astrike={true}
                fetchIncrementalData={(search: any) =>
                  handleClientSearchChange(
                    search,
                    `client_options`,
                    formRenderProps
                  )
                }
                options={
                  formRenderProps
                    .valueGetter("client_options")
                    ?.map((client: any) => {
                      return {
                        value: client.ID,
                        label: `${client?.FirstName} ${client?.LastName}`,
                      };
                    }) || []
                }
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="ExpectedValue"
                label="Expected Value"
                placeholder="Expected Value"
                astrike={true}
                component={FormNumericTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"OpportunityTypeID"}
                name={"OpportunityTypeID"}
                label={"Opportunity Type"}
                astrike={true}
                component={FormSelectionField}
                validator={requiredValidator}
                placeholder="Opportunity Type"
                options={OpportunityTypeList?.map(
                  (opportunity_type: IOpportunityType) => {
                    return {
                      value: opportunity_type?.ID,
                      label: opportunity_type?.OpportunityType,
                    };
                  }
                )}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"SetPriority"}
                name={"SetPriority"}
                label={"Priority"}
                astrike={true}
                component={FormSelectionField}
                validator={requiredValidator}
                placeholder="Priority"
                options={OpportunityPriority?.map((priority: any) => {
                  return {
                    value: priority?.value,
                    label: priority?.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="FollowupDate"
                label="Follow-up Date"
                component={FormDatePicker}
                validator={requiredValidator}
                minDate={moment().toDate()}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"AssignTeamID"}
                name={"AssignTeamID"}
                label={"Assign Team"}
                placeholder="Assign Team"
                astrike={true}
                component={FormSelectionField}
                validator={requiredValidator}
                options={UserList?.map((user: IUser) => {
                  return {
                    value: user?.ID,
                    label: `${user?.FirstName} ${user?.LastName}`,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"IsActive"}
                component={FormSelectionField}
                validator={requiredValidator}
                astrike={true}
                label={"Status"}
                placeholder="Status"
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                marginTop: 20,
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={OpportunityGUID ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
          <UserChangeWatcher formRenderProps={formRenderProps} />
        </FormElement>
      )}
    />
  );
};

export default CreateOpportunity;
